import {
  RumEventDomainContext,
  RumEvent,
  datadogRum,
} from "@datadog/browser-rum"
import {
  canInitializeDatadog,
  commonDatadogParams,
} from "@/helpers/datadogHelper"
import { isServerError } from "@/api/utils/HttpClient"
import { isBackofficeApiError } from "@/api/backofficeRest/backofficeApiError"
import { safeJSONParse } from "@/helpers/stringHelpers"
import { AxiosError } from "axios"
import {
  sensitiveFieldNames,
  REDACTED_PLACEHOLDER,
} from "@/constants/errorReporting"

export function redactSensitiveFields<T>(data: T): T {
  if (typeof data === "string") {
    let sanitized: string = data
    // Redact sensitive field names and their values from strings
    sensitiveFieldNames.forEach((fieldName) => {
      const patterns = [
        // JSON-style: "fieldName": "value"
        new RegExp(String.raw`"${fieldName}"\s*:\s*"[^"]*"`, "gi"),
        // Single quotes: 'fieldName': 'value'
        new RegExp(String.raw`'${fieldName}'\s*:\s*'[^']*'`, "gi"),
        // Unquoted with colon: fieldName: value
        new RegExp(String.raw`\b${fieldName}\s*:\s*[^,\s}\]]+`, "gi"),
        // Equals: fieldName=value
        new RegExp(String.raw`\b${fieldName}\s*=\\s*[^,\s&}\]]+`, "gi"),
        // GraphQL variable style: $fieldName or "fieldName":"value"
        new RegExp(String.raw`\$${fieldName}\s*:\s*[^,\s}\]]+`, "gi"),
      ]
      patterns.forEach((pattern) => {
        sanitized = sanitized.replace(
          pattern,
          `${fieldName}: ${REDACTED_PLACEHOLDER}`,
        )
      })
    })
    return sanitized as T
  }
  if (Array.isArray(data)) {
    return data.map((item) => redactSensitiveFields(item)) as T
  }
  if (typeof data === "object" && data !== null) {
    const obfuscatedData: Record<string, unknown> = {}
    for (const fieldName in data) {
      const fieldValue = data[fieldName]
      // eslint-disable-next-line no-prototype-builtins
      if (data.hasOwnProperty(fieldName)) {
        if (typeof fieldValue === "object") {
          obfuscatedData[fieldName] = redactSensitiveFields(fieldValue)
        } else if (sensitiveFieldNames.includes(fieldName)) {
          obfuscatedData[fieldName] = REDACTED_PLACEHOLDER
        } else {
          obfuscatedData[fieldName] = redactSensitiveFields(fieldValue)
        }
      }
    }
    return obfuscatedData as T
  }
  return data
}

const setRequestMetadata = (event: RumEvent, error: AxiosError) => {
  const responseData = error?.response?.data
  const requestData = error?.config?.data
    ? safeJSONParse(error?.config?.data)
    : null
  event.context = event.context || {}
  if (responseData) {
    event.context.responseData = redactSensitiveFields(responseData)
  }
  if (requestData) {
    event.context.requestData = redactSensitiveFields(requestData)
  }
}

const redactErrorEvent = (event: RumEvent) => {
  if (event.error && typeof event.error === "object") {
    const errorObj = event.error as { message?: string; stack?: string }
    if (errorObj.message) {
      errorObj.message = redactSensitiveFields(errorObj.message)
    }
    if (errorObj.stack) {
      errorObj.stack = redactSensitiveFields(errorObj.stack)
    }
  }

  if (event.context) {
    event.context = redactSensitiveFields(event.context)
  }
}

export default {
  install(app) {
    if (canInitializeDatadog()) {
      datadogRum.init({
        ...commonDatadogParams,
        applicationId: import.meta.env.SO_DATADOG_APPLICATION_ID,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
        allowedTracingUrls: [
          {
            match: /https:\/\/.*\.staging.soenergy\.co\/.*/,
            propagatorTypes: ["tracecontext"],
          },
          {
            match: /https:\/\/.*\.preview\.soenergy\.co\/.*/,
            propagatorTypes: ["tracecontext"],
          },
          {
            match: /https:\/\/.*\.pre-production\.soenergy\.co\/.*/,
            propagatorTypes: ["tracecontext"],
          },
          {
            match: /https:\/\/.*\.so\.energy\/.*/,
            propagatorTypes: ["tracecontext"],
          },
        ],
        beforeSend: (event: RumEvent, context: RumEventDomainContext) => {
          const error = "error" in context ? context?.error : null
          const requestError = isBackofficeApiError(error) ? error.cause : error
          if (isServerError(requestError)) {
            setRequestMetadata(event, requestError)
          }

          // Redact sensitive data from all error events
          if (event.type === "error") {
            redactErrorEvent(event)
          }

          return true
        },
      })
      datadogRum.setGlobalContext({
        git: {
          commit: {
            sha: __APP_GIT_COMMIT_SHA__,
          },
          repository_url: "https://www.github.com/soenergy/fe-nova",
        },
      })
      const originalErrorHandler = app.config.errorHandler
      app.config.errorHandler = function (error: Error, vm, info) {
        if (typeof originalErrorHandler === "function") {
          originalErrorHandler.call(this, error, vm, info)
        }
        datadogRum.addError(error, {
          error: { kind: error.name, stack: error.stack },
          context: { info },
        })
      }
    }
  },
}

export const recordEventToDatadog = (eventName: string, metadata?: object) => {
  if (canInitializeDatadog()) {
    datadogRum.addAction(eventName, metadata)
  }
}
